/**
 * Jain Gaurav Yatra — Admin booking list, detail, and updates.
 * Admin JWT authentication, with the existing tenant header.
 */
import { extractAdminTokens, getStoredAdminSession } from "@/lib/admin-login-api";

function apiBase(): string {
  return (import.meta.env.VITE_API_BASE_URL as string | undefined)?.replace(/\/$/, "") ?? "";
}

function tenantId(): string {
  return (import.meta.env.VITE_X_TENANTID as string | undefined) ?? "";
}

function jsonHeaders(): HeadersInit {
  const id = tenantId();
  const { access } = extractAdminTokens(getStoredAdminSession()?.session);
  return {
    "Content-Type": "application/json",
    ...(id ? { "X-Tenant-ID": id } : {}),
    ...(access ? { Authorization: `Bearer ${access}` } : {}),
  };
}

function errMessage(data: unknown, status: number): string {
  const messages = collectApiErrors(data);
  if (messages.length > 0) {
    return messages.join(" ");
  }
  return `Something went wrong (${status}). Please try again.`;
}

function collectApiErrors(data: unknown): string[] {
  if (!data || typeof data !== "object") {return [];}
  const d = data as Record<string, unknown>;
  const out: string[] = [];
  const seen = new Set<string>();

  const add = (msg: string) => {
    const cleaned = msg.trim();
    if (!cleaned || cleaned === "Validation error." || seen.has(cleaned)) {return;}
    seen.add(cleaned);
    out.push(humanizeApiError(cleaned));
  };

  const flatten = (v: unknown): string[] => {
    if (typeof v === "string") {return v.trim() ? [v.trim()] : [];}
    if (Array.isArray(v)) {return v.flatMap(flatten);}
    if (v && typeof v === "object") {
      return Object.values(v as Record<string, unknown>).flatMap(flatten);
    }
    return [];
  };

  const pickStr = (v: unknown) => (typeof v === "string" ? v.trim() : "");

  const nested = (d.error && typeof d.error === "object"
    ? (d.error as Record<string, unknown>)
    : null);

  add(pickStr(d.detail));
  add(pickStr(d.message));
  if (nested) {
    add(pickStr(nested.message));
    add(pickStr(nested.detail));
    for (const msg of flatten(nested.details ?? nested.errors)) {
      add(msg);
    }
  }
  for (const msg of flatten(d.details ?? d.errors)) {
    add(msg);
  }

  // DRF-style field errors at the top level, e.g. { guide_id: ["…"] }
  for (const [key, val] of Object.entries(d)) {
    if (["error", "message", "detail", "details", "errors", "status", "code"].includes(key)) {
      continue;
    }
    for (const msg of flatten(val)) {
      add(key.endsWith("_id") ? msg : `${key.replace(/_/g, " ")}: ${msg}`);
    }
  }

  return out;
}

function humanizeApiError(message: string): string {
  const m = message.toLowerCase();
  if (m.includes("requested guide gender is 'female'") && m.includes("guide is 'male'")) {
    return "This devotee requested a female guide. Please choose a female guide.";
  }
  if (m.includes("requested guide gender is 'male'") && m.includes("guide is 'female'")) {
    return "This devotee requested a male guide. Please choose a male guide.";
  }
  if (m.includes("guide gender")) {
    return message.replace(/^The requested guide gender is/i, "Devotee requested").replace(
      /but this guide is/i,
      "but the selected guide is",
    );
  }
  if (m.includes("does not include a guide") || m.includes("walk does not include")) {
    return "This walk does not include a guide assignment.";
  }
  return message;
}

/** Read + write both use visit-bookings; `/admin/bookings/` is list-only with no detail route. */
const VISIT_BOOKINGS_PATH = "/jain_gaurav_yatra/visit-bookings/";
const ADMIN_BOOKINGS_PATH = "/jain_gaurav_yatra/admin/bookings/";

/** Page-facing booking shape (display fields resolved up front). */
export interface Booking {
  id: string;
  type: string;
  member_name: string;
  member_phone: string;
  /** Devotee email from the nested devotee/member object. */
  member_email: string;
  provider_name: string;
  /** Walk this booking is for, when the API supplies one. */
  walk_name: string;
  /** Assigned guide, when the API supplies one. */
  guide_name: string;
  guide_phone: string;
  guide_email: string;
  /** Assigned volunteer details, when the API supplies them. */
  volunteer_name: string;
  volunteer_phone: string;
  volunteer_email: string;
  volunteer_gender: string;
  /** Volunteer acceptance state (read-only). */
  volunteer_status: string;
  volunteer_accepted_at: string | null;
  is_volunteer_walk: boolean | null;
  /** Walk pricing / meeting point (read-only, from the nested walk). */
  walk_price: number;
  walk_is_free: boolean | null;
  meet_up: string;
  /** Read-only label selected at booking time; backend includes the walk default. */
  readonly starting_point: string;
  booking_date: string;
  booking_time: string;
  status: string;
  notes: string | null;
  group_size: number | null;
  visitor_names: string | null;
  confirmed_at: string | null;
  created_at: string;
  updated_at: string;
  /** Cancellation / refund policy (read-only). */
  is_free_cancellation: boolean | null;
  cancellation_charge_now: number;

  // Edit-form fields. Key names are inferred from the admin design; `toBooking` accepts a few
  // plausible spellings so whichever the API uses will populate.
  walk_id: string;
  /**
   * What the booked walk actually includes (from the nested walk). `null` means unknown — the
   * list endpoint may omit the walk — so callers should only restrict UI on an explicit false.
   */
  walk_is_guide: boolean | null;
  walk_is_breakfast: boolean | null;
  walk_is_volunteer: boolean | null;
  /** Server-rendered labels, e.g. "Early Morning (6 - 8 AM)" / "Confirmed". */
  time_slot_display: string;
  status_display: string;
  meeting_point: string;
  adults: number;
  children: number;
  seniors: number;
  preferred_volunteer_gender: string;
  preferred_guide_gender: string;
  guide_id: string;
  volunteer_id: string;
  rickshaw_id: string;
  breakfast_id: string;
  /** Read-only — comes from the payment at booking time. */
  amount_paid: number;
  final_amount: number;
  payment_status: string;
  payment_method: string;
  cancelled_at: string | null;
  cancellation_charge: number;
  refund_amount: number;
}

/**
 * Fields the admin edit form can write back — mirrors VisitBookingSerializer.
 * `walk` is fixed at booking time and `amount_paid` is read-only, so neither is writable.
 */
export interface BookingWrite {
  status: string;
  visit_date: string;
  time_slot: string;
  meeting_point: string;
  visitor_count: number;
  adults: number;
  children: number;
  seniors: number;
  member_names: string;
  preferred_volunteer_gender: string;
  preferred_guide_gender: string;
  guide_id: string;
  volunteer_id: string;
  rickshaw_id: string;
  breakfast_id: string;
  final_amount: number;
  payment_status: string;
  payment_method: string;
  notes: string;
}

function str(v: unknown): string {
  return v === null || v === undefined ? "" : String(v);
}

function strOrNull(v: unknown): string | null {
  if (v === null || v === undefined || v === "") {return null;}
  return String(v);
}

function obj(v: unknown): Record<string, unknown> {
  return v && typeof v === "object" ? (v as Record<string, unknown>) : {};
}

/** Tolerant mapping — accepts flat (member_name) or nested (member.full_name) shapes. */
function toBooking(row: Record<string, unknown>): Booking {
  const member = obj(row.member ?? row.user ?? row.devotee);
  const volunteer = obj(row.volunteer);
  const guide = obj(row.guide);
  const walk = obj(row.walk);
  const rickshaw = obj(row.rickshaw);
  const type = str(row.type ?? row.booking_type);

  const providerName =
    str(row.provider_name) ||
    str(row.volunteer_name) ||
    str(volunteer.name) ||
    str(row.rickshaw_name) ||
    str(rickshaw.driver_name) ||
    (type === "visit" ? "Temple Visit" : "");

  const groupSizeRaw = row.group_size ?? row.visitor_count;
  const groupSize =
    groupSizeRaw === null || groupSizeRaw === undefined ? null : Number(groupSizeRaw) || null;

  return {
    id: str(row.id),
    type,
    member_name:
      str(row.member_name) || str(member.full_name) || str(member.name) || "Devotee",
    member_phone: str(row.member_phone) || str(member.phone) || "",
    member_email: str(row.member_email) || str(member.email),
    provider_name: providerName,
    walk_name: str(row.walk_name) || str(walk.title) || str(row.walk_title),
    guide_name: str(row.guide_name) || str(guide.full_name) || str(guide.name),
    guide_phone: str(guide.phone),
    guide_email: str(guide.email),
    volunteer_name: str(volunteer.name) || str(row.volunteer_name),
    volunteer_phone: str(volunteer.phone),
    volunteer_email: str(volunteer.email),
    volunteer_gender: str(volunteer.gender),
    volunteer_status: str(row.volunteer_status),
    volunteer_accepted_at: strOrNull(row.volunteer_accepted_at),
    is_volunteer_walk: boolOrNull(row.is_volunteer_walk ?? walk.is_volunteer),
    walk_price: numOr0(walk.price ?? row.walk_price),
    walk_is_free: boolOrNull(walk.is_free),
    meet_up: str(row.meet_up) || str(walk.meet_up),
    starting_point: str(row.starting_point),
    // The detail serializer calls these visit_date/time_slot; the admin list uses booking_*.
    booking_date: str(row.booking_date ?? row.visit_date),
    booking_time: str(row.booking_time ?? row.time_slot),
    status: str(row.status),
    notes: strOrNull(row.notes),
    group_size: groupSize,
    // member_names is a string[] on the wire; the editor edits it as one-per-line text.
    visitor_names: Array.isArray(row.member_names)
      ? row.member_names.map(str).filter(Boolean).join("\n") || null
      : strOrNull(row.visitor_names ?? row.member_names),
    confirmed_at: strOrNull(row.confirmed_at),
    created_at: str(row.created_at),
    updated_at: str(row.updated_at),
    is_free_cancellation: boolOrNull(row.is_free_cancellation),
    cancellation_charge_now: numOr0(row.cancellation_charge_if_cancelled_now),

    walk_id: str(obj(row.walk).id ?? row.walk_id),
    walk_is_guide: boolOrNull(obj(row.walk).is_guide),
    walk_is_breakfast: boolOrNull(obj(row.walk).is_breakfast),
    walk_is_volunteer: boolOrNull(obj(row.walk).is_volunteer),
    time_slot_display: str(row.time_slot_display),
    status_display: str(row.status_display),
    meeting_point: str(row.meeting_point),
    adults: numOr0(row.adults),
    children: numOr0(row.children),
    seniors: numOr0(row.seniors),
    preferred_volunteer_gender: str(row.preferred_volunteer_gender) || "any",
    preferred_guide_gender: str(row.preferred_guide_gender) || "any",
    guide_id: str(obj(row.guide).id ?? row.guide_id),
    volunteer_id: str(obj(row.volunteer).id ?? row.volunteer_id),
    rickshaw_id: str(obj(row.rickshaw).id ?? row.rickshaw_id),
    breakfast_id: str(obj(row.breakfast_option).id ?? row.breakfast_id),
    /** Read-only: set from the Razorpay payment at booking time. */
    amount_paid: numOr0(row.amount_paid),
    final_amount: numOr0(row.final_amount),
    payment_status: str(row.payment_status),
    payment_method: str(row.payment_method),
    cancelled_at: strOrNull(row.cancelled_at),
    cancellation_charge: numOr0(row.cancellation_charge),
    refund_amount: numOr0(row.refund_amount),
  };
}

/** Preserves "unknown" (undefined/absent) as null rather than collapsing it to false. */
function boolOrNull(v: unknown): boolean | null {
  return v === undefined || v === null ? null : v === true;
}

function numOr0(v: unknown): number {
  if (v === null || v === undefined || v === "") {return 0;}
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
}

/**
 * Partial update — sends only the given keys. Used for inline table edits (e.g. assigning a guide)
 * so one field can change without re-submitting, and possibly clobbering, the whole booking.
 */
export async function patchBooking(
  id: string,
  fields: Record<string, unknown>,
): Promise<Booking> {
  const base = apiBase();
  if (!base) {throw new Error("API base URL not configured (VITE_API_BASE_URL).");}
  const res = await fetch(`${base}${VISIT_BOOKINGS_PATH}${id}/`, {
    method: "PATCH",
    headers: jsonHeaders(),
    credentials: "include",
    body: JSON.stringify(fields),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {throw new Error(errMessage(data, res.status));}
  return toBooking(data as Record<string, unknown>);
}

/**
 * Update a booking.
 *
 * NOTE the path: `/admin/bookings/` is list-only and has no detail route (PATCHing it returns a
 * Django HTML 404, not a DRF one). Writes go to `/visit-bookings/{id}/`, which is where
 * VisitBookingSerializer is registered.
 *
 * `walk` is fixed at booking time — the serializer pops it on update — so it is never sent.
 * Empty relation ids are sent as null to clear the assignment.
 */
export async function updateBooking(id: string, p: BookingWrite): Promise<Booking> {
  const base = apiBase();
  if (!base) {throw new Error("API base URL not configured (VITE_API_BASE_URL).");}
  const rel = (v: string) => (v ? (/^\d+$/.test(v) ? Number(v) : v) : null);
  const res = await fetch(`${base}${VISIT_BOOKINGS_PATH}${id}/`, {
    method: "PATCH",
    headers: jsonHeaders(),
    credentials: "include",
    body: JSON.stringify({
      status: p.status,
      visit_date: p.visit_date,
      time_slot: p.time_slot,
      meeting_point: p.meeting_point,
      visitor_count: p.visitor_count,
      adults: p.adults,
      children: p.children,
      seniors: p.seniors,
      // Sent as an array — one entry per line from the textarea.
      member_names: p.member_names
        .split("\n")
        .map((s) => s.trim())
        .filter(Boolean),
      preferred_volunteer_gender: p.preferred_volunteer_gender,
      preferred_guide_gender: p.preferred_guide_gender,
      guide_id: rel(p.guide_id),
      volunteer_id: rel(p.volunteer_id),
      rickshaw_id: rel(p.rickshaw_id),
      breakfast_id: rel(p.breakfast_id),
      final_amount: p.final_amount,
      payment_status: p.payment_status,
      payment_method: p.payment_method,
      notes: p.notes,
    }),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {throw new Error(errMessage(data, res.status));}
  return toBooking(data as Record<string, unknown>);
}

export async function listBookings(): Promise<Booking[]> {
  const base = apiBase();
  if (!base) {throw new Error("API base URL not configured (VITE_API_BASE_URL).");}
  const res = await fetch(`${base}${ADMIN_BOOKINGS_PATH}`, {
    method: "GET",
    headers: jsonHeaders(),
    credentials: "include",
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {throw new Error(errMessage(data, res.status));}
  const rows = Array.isArray(data)
    ? data
    : Array.isArray((data as { results?: unknown[] })?.results)
      ? (data as { results: unknown[] }).results
      : [];
  return (rows as Record<string, unknown>[]).map(toBooking);
}

export async function getBooking(id: string): Promise<Booking> {
  const base = apiBase();
  if (!base) {throw new Error("API base URL not configured (VITE_API_BASE_URL).");}
  const res = await fetch(`${base}${VISIT_BOOKINGS_PATH}${encodeURIComponent(id)}/`, {
    method: "GET",
    headers: jsonHeaders(),
    credentials: "include",
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {throw new Error(errMessage(data, res.status));}
  // The detail serializer may omit the discriminator supplied by the admin list.
  return toBooking({ ...data, type: data.type || "visit" });
}
